// downloadSingleProductCSV.js

import { saveAs } from "file-saver"
import Papa from "papaparse"

async function fetchSingleProductData(productUrl) {
  try {
    const response = await fetch(productUrl)
    if (!response.ok) {
      console.error(`Failed to fetch product data from ${productUrl}:`, response.status)
      return null
    }
    const data = await response.json()
    return data.product // Assuming the product data is nested under a 'product' key
  } catch (error) {
    console.error("Error fetching single product data:", error)
    return null
  }
}

async function fetchCollectionProductsData(collectionUrl) {
  let allProducts = []
  let page = 1
  let hasNextPage = true

  try {
    while (hasNextPage) {
      const url = `${collectionUrl}?page=${page}`
      const response = await fetch(url)
      if (!response.ok) {
        console.error(`Failed to fetch collection products from ${url}:`, response.status)
        break
      }
      const data = await response.json()
      if (data.products && data.products.length > 0) {
        allProducts = [...allProducts, ...data.products]
        page++
        if (data.products.length < 50) {
          // Assuming 50 is the max products per page
          hasNextPage = false
        }
      } else {
        hasNextPage = false
      }
    }
    return allProducts
  } catch (error) {
    console.error("Error fetching all collection products:", error)
    return null
  }
}

function formatProductToRow(productData, platform) {
  if (!productData) {
    return null
  }

  if (platform === "Shopify") {
    // UPDATED: New Shopify format matching our previous implementation
    const shopifyHeaders = [
      "Handle",
      "Title",
      "Body (HTML)",
      "Vendor",
      "Type",
      "Tags",
      "Published",
      "Option1 Name",
      "Option1 Value",
      "Option2 Name",
      "Option2 Value",
      "Option3 Name",
      "Option3 Value",
      "Variant SKU",
      "Variant Grams",
      "Variant Inventory Tracker",
      "Variant Inventory Qty",
      "Variant Inventory Policy",
      "Variant Fulfillment Service",
      "Variant Price",
      "Variant Compare At Price",
      "Variant Requires Shipping",
      "Variant Taxable",
      "Variant Barcode",
      "Image Src",
      "Image Position",
      "Image Alt Text",
      "Gift Card",
    ]

    const allRows = []

    // First, add all variants
    productData.variants.forEach((variant) => {
      const variantRow = {
        Handle: productData.handle || "",
        Title: productData.title || "",
        "Body (HTML)": productData.body_html || "",
        Vendor: productData.vendor || "",
        Type: productData.product_type || "",
        Tags: Array.isArray(productData.tags) ? productData.tags.join(",") : "",
        Published: "TRUE",
        "Option1 Name": productData.options && productData.options[0] ? productData.options[0].name : "",
        "Option1 Value": variant.option1 || "",
        "Option2 Name": productData.options && productData.options[1] ? productData.options[1].name : "",
        "Option2 Value": variant.option2 || "",
        "Option3 Name": productData.options && productData.options[2] ? productData.options[2].name : "",
        "Option3 Value": variant.option3 || "",
        "Variant SKU": variant.sku || "",
        "Variant Grams": String(variant.grams || ""),
        "Variant Inventory Tracker": "shopify",
        "Variant Inventory Qty": "1",
        "Variant Inventory Policy": "deny",
        "Variant Fulfillment Service": "manual",
        "Variant Price": String(variant.price || ""),
        "Variant Compare At Price": String(variant.compare_at_price || ""),
        "Variant Requires Shipping": variant.requires_shipping ? "TRUE" : "FALSE",
        "Variant Taxable": variant.taxable ? "TRUE" : "FALSE",
        "Variant Barcode": variant.barcode || "",
        "Image Src": "",
        "Image Position": "",
        "Image Alt Text": "",
        "Gift Card": "FALSE",
      }
      allRows.push(variantRow)
    })

    // Then, add all images
    if (productData.images && productData.images.length > 0) {
      productData.images.forEach((image, imageIndex) => {
        const imageRow = {
          Handle: productData.handle || "",
          Title: "",
          "Body (HTML)": "",
          Vendor: "",
          Type: "",
          Tags: "",
          Published: "",
          "Option1 Name": "",
          "Option1 Value": "",
          "Option2 Name": "",
          "Option2 Value": "",
          "Option3 Name": "",
          "Option3 Value": "",
          "Variant SKU": "",
          "Variant Grams": "",
          "Variant Inventory Tracker": "",
          "Variant Inventory Qty": "",
          "Variant Inventory Policy": "",
          "Variant Fulfillment Service": "",
          "Variant Price": "",
          "Variant Compare At Price": "",
          "Variant Requires Shipping": "",
          "Variant Taxable": "",
          "Variant Barcode": "",
          "Image Src": image.src || "",
          "Image Position": String(imageIndex + 1),
          "Image Alt Text": "",
          "Gift Card": "",
        }
        allRows.push(imageRow)
      })
    }

    return allRows
  } else if (platform === "WooCommerce") {
    // WooCommerce format remains unchanged
    const rows = []
    const cleanedDescription = productData.body_html || productData.handle || ""
    const allImages = productData.images ? productData.images.map((img) => img.src).join(",") : ""
    const productTags = Array.isArray(productData.tags) ? productData.tags.join(",") : ""

    if (productData.variants.length > 1) {
      // Variable product - main product row
      rows.push({
        ID: productData.id,
        Type: "variable",
        SKU: productData.handle || "",
        Name: productData.title,
        "Short description": productData.handle || "",
        Description: cleanedDescription,
        "In stock?": 1,
        Stock: "",
        "Sale price": "",
        "Regular price": "",
        Categories: productData.product_type || "",
        Tags: productTags,
        Vendor: productData.vendor || "",
        Images: allImages,
        "Parent ID": "",
        "Attribute 1 name":
          productData.options && productData.options[0] && productData.options[0].name
            ? productData.options[0].name
            : "",
        "Attribute 1 value(s)":
          productData.options && productData.options[0] && productData.options[0].values
            ? productData.options[0].values.join("|")
            : "",
        "Attribute 2 name":
          productData.options && productData.options[1] && productData.options[1].name
            ? productData.options[1].name
            : "",
        "Attribute 2 value(s)":
          productData.options && productData.options[1] && productData.options[1].values
            ? productData.options[1].values.join("|")
            : "",
        "Attribute 3 name":
          productData.options && productData.options[2] && productData.options[2].name
            ? productData.options[2].name
            : "",
        "Attribute 3 value(s)":
          productData.options && productData.options[2] && productData.options[2].values
            ? productData.options[2].values.join("|")
            : "",
      })
      // Variable product - variation rows
      productData.variants.forEach((variant) => {
        const variantImage = variant.featured_image ? variant.featured_image.src : ""
        rows.push({
          ID: variant.id,
          Type: "variation",
          SKU: variant.sku || "",
          Name: `${productData.title} - ${variant.title}` || productData.title,
          "Short description": productData.handle || "",
          Description: "",
          "In stock?": variant.available ? 1 : 0,
          Stock: "",
          "Sale price": variant.compare_at_price || "",
          "Regular price": variant.price || "",
          Categories: "",
          Tags: "",
          Vendor: productData.vendor || "",
          Images: variantImage,
          "Parent ID": `id:${productData.id}`,
          "Attribute 1 name":
            productData.options && productData.options[0] && productData.options[0].name
              ? productData.options[0].name
              : "",
          "Attribute 1 value(s)": variant.option1 || "",
          "Attribute 2 name":
            productData.options && productData.options[1] && productData.options[1].name
              ? productData.options[1].name
              : "",
          "Attribute 1 value(s)": variant.option2 || "",
          "Attribute 3 name":
            productData.options && productData.options[2] && productData.options[2].name
              ? productData.options[2].name
              : "",
          "Attribute 1 value(s)": variant.option3 || "",
        })
      })
    } else if (productData.variants.length === 1) {
      // Simple product
      const variant = productData.variants[0]
      rows.push({
        ID: productData.id,
        Type: "simple",
        SKU: variant.sku || productData.handle || "",
        Name: productData.title,
        "Short description": productData.handle || "",
        Description: cleanedDescription,
        "In stock?": variant.available ? 1 : 0,
        Stock: "",
        "Sale price": variant.compare_at_price || "",
        "Regular price": variant.price || "",
        Categories: productData.product_type || "",
        Tags: productTags,
        Vendor: productData.vendor || "",
        Images: allImages,
        "Parent ID": "",
        "Attribute 1 name":
          productData.options && productData.options[0] && productData.options[0].name
            ? productData.options[0].name
            : "",
        "Attribute 1 value(s)": variant.option1 || "",
        "Attribute 2 name":
          productData.options && productData.options[1] && productData.options[1].name
            ? productData.options[1].name
            : "",
        "Attribute 1 value(s)": variant.option2 || "",
        "Attribute 3 name":
          productData.options && productData.options[2] && productData.options[2].name
            ? productData.options[2].name
            : "",
        "Attribute 1 value(s)": variant.option3 || "",
      })
    }
    return rows
  }
  return []
}

const downloadSingleProductCSV = async (baseURL, productHandle, platform) => {
  const productUrl = `${baseURL}/products/${productHandle}.json`
  const productData = await fetchSingleProductData(productUrl)

  if (productData) {
    const rows = formatProductToRow(productData, platform)
    if (rows) {
      let headers
      if (platform === "Shopify") {
        headers = [
          "Handle",
          "Title",
          "Body (HTML)",
          "Vendor",
          "Type",
          "Tags",
          "Published",
          "Option1 Name",
          "Option1 Value",
          "Option2 Name",
          "Option2 Value",
          "Option3 Name",
          "Option3 Value",
          "Variant SKU",
          "Variant Grams",
          "Variant Inventory Tracker",
          "Variant Inventory Qty",
          "Variant Inventory Policy",
          "Variant Fulfillment Service",
          "Variant Price",
          "Variant Compare At Price",
          "Variant Requires Shipping",
          "Variant Taxable",
          "Variant Barcode",
          "Image Src",
          "Image Position",
          "Image Alt Text",
          "Gift Card",
        ]
      } else if (platform === "WooCommerce") {
        headers = [
          "ID",
          "Name",
          "Type",
          "SKU",
          "Short description",
          "Description",
          "In stock?",
          "Stock",
          "Sale price",
          "Regular price",
          "Categories",
          "Tags",
          "Vendor",
          "Images",
          "Parent ID",
          "Attribute 1 name",
          "Attribute 1 value(s)",
          "Attribute 2 name",
          "Attribute 2 value(s)",
          "Attribute 3 name",
          "Attribute 3 value(s)",
        ]
      }
      const csvData = Papa.unparse({ fields: headers, data: rows })
      const filename = `${productData.handle}_${platform}.csv`
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" })
      saveAs(blob, filename)
    } else {
      alert("Error formatting product data for CSV.")
    }
  } else {
    alert("Failed to retrieve product data for download.")
  }
}

const downloadCollectionProductsCSV = async (baseURL, collectionHandle, platform) => {
  const collectionUrl = `${baseURL}/collections/${collectionHandle}/products.json`
  const allCollectionProducts = await fetchCollectionProductsData(collectionUrl)

  if (allCollectionProducts && allCollectionProducts.length > 0) {
    let allRows = []
    for (const product of allCollectionProducts) {
      const rows = formatProductToRow(product, platform)
      if (rows) {
        allRows = [...allRows, ...rows]
      }
    }

    if (allRows.length > 0) {
      let headers
      if (platform === "Shopify") {
        headers = [
          "Handle",
          "Title",
          "Body (HTML)",
          "Vendor",
          "Type",
          "Tags",
          "Published",
          "Option1 Name",
          "Option1 Value",
          "Option2 Name",
          "Option2 Value",
          "Option3 Name",
          "Option3 Value",
          "Variant SKU",
          "Variant Grams",
          "Variant Inventory Tracker",
          "Variant Inventory Qty",
          "Variant Inventory Policy",
          "Variant Fulfillment Service",
          "Variant Price",
          "Variant Compare At Price",
          "Variant Requires Shipping",
          "Variant Taxable",
          "Variant Barcode",
          "Image Src",
          "Image Position",
          "Image Alt Text",
          "Gift Card",
        ]
      } else if (platform === "WooCommerce") {
        headers = [
          "ID",
          "Name",
          "Type",
          "SKU",
          "Short description",
          "Description",
          "In stock?",
          "Stock",
          "Sale price",
          "Regular price",
          "Categories",
          "Tags",
          "Vendor",
          "Images",
          "Parent ID",
          "Attribute 1 name",
          "Attribute 1 value(s)",
          "Attribute 2 name",
          "Attribute 2 value(s)",
          "Attribute 3 name",
          "Attribute 3 value(s)",
        ]
      }
      const csvData = Papa.unparse({ fields: headers, data: allRows })
      const filename = `${collectionHandle}_${platform}.csv`
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8" })
      saveAs(blob, filename)
    } else {
      alert("No product data found in this collection to download.")
    }
  } else {
    alert("Failed to retrieve product data for this collection.")
  }
}

export { downloadSingleProductCSV, downloadCollectionProductsCSV }
